import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { fetchEvents, type EventSummary } from '@features/events/api';
import { formatDate } from '@shared/lib/format';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/** Home widget: the next two upcoming events, tap-through to the events module. */
export function EventsWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['events', 'home-widget'], queryFn: () => fetchEvents() });

  const upcoming = upcomingEvents(query.data ?? [], Date.now());

  return (
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel={upcoming.length > 0 ? 'Show all' : undefined}
      onAction={() => navigate('/app/events')}
      onClick={() => navigate('/app/events')}
      className={upcoming.length > 0 ? 'gap-0 py-1 px-4' : undefined}
    >
      {query.isLoading ? (
        <div className="flex flex-col gap-2.5">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      ) : upcoming.length === 0 ? (
        <p className="text-body-sm text-ink-muted">Nothing on the calendar yet.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {upcoming.map((event) => (
            <li key={event.event_id}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/app/events');
                }}
                className="flex w-full items-center gap-3 py-3 text-left active:opacity-80"
              >
                {/* Date block */}
                <span className="flex h-[3.25rem] w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-sunken">
                  <span className="text-[0.6875rem] font-bold uppercase leading-none tracking-[0.08em] text-[color-mix(in_oklab,var(--accent-brass)_65%,var(--ink-primary))]">
                    {formatDate(event.starts_at, 'MMM')}
                  </span>
                  <span className="mt-1 text-headline font-bold leading-none text-ink">
                    {formatDate(event.starts_at, 'dd')}
                  </span>
                </span>

                <span className="min-w-0 flex-1">
                  <MarqueeText className="text-[0.9375rem] font-semibold text-ink">{event.title}</MarqueeText>
                  <MarqueeText className="text-[0.8125rem] text-ink-secondary">{formatEventLine(event)}</MarqueeText>
                </span>

                <ChevronRight className="h-4.5 w-4.5 shrink-0 text-ink-muted" strokeWidth={2} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

/** The next two events, soonest first — including ones that started within the last hour. */
export function upcomingEvents<T extends Pick<EventSummary, 'starts_at'>>(events: T[], now: number): T[] {
  const cutoff = now - 60 * 60 * 1000;
  return events
    .filter((e) => new Date(e.starts_at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .slice(0, 2);
}

/** "Sat · 09:00 – 13:00 · Clubhouse" (end time only when it's the same day). */
export function formatEventLine(
  event: Pick<EventSummary, 'starts_at' | 'ends_at' | 'all_day' | 'location'>,
): string {
  const day = formatDate(event.starts_at, 'EEE');
  const sameDayEnd =
    event.ends_at && formatDate(event.ends_at, 'yyyy-MM-dd') === formatDate(event.starts_at, 'yyyy-MM-dd');
  const time = event.all_day
    ? 'All day'
    : [formatDate(event.starts_at, 'HH:mm'), sameDayEnd ? formatDate(event.ends_at!, 'HH:mm') : '']
        .filter(Boolean)
        .join(' – ');
  return [day, time, event.location].filter(Boolean).join(' · ');
}
